/**
 * YogaVideoGallery — Multi-video library for a yoga pose with:
 *   - YouTube iframe embeds (default + admin-added)
 *   - Custom MP4 uploads (admin)
 *   - Per-video Lock / Delete / Replace (admin)
 *   - Fullscreen adaptive to phone orientation (uses Fullscreen API)
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, Lock, Unlock, Upload, Youtube, Maximize2, X, Video, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import useYogaVideos, { extractYouTubeId } from '../hooks/useYogaVideos';

const API = process.env.REACT_APP_BACKEND_URL;

function FullscreenPlayer({ video, onClose }) {
  const containerRef = useRef(null);

  // Request fullscreen + try to lock orientation to landscape on mobile for better viewing
  useEffect(() => {
    const el = containerRef.current;
    if (el?.requestFullscreen) {
      el.requestFullscreen().catch(() => {});
    } else if (el?.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
    }
    // Try to lock orientation (landscape on mobile for video viewing)
    if (window.screen?.orientation?.lock) {
      window.screen.orientation.lock('landscape').catch(() => {});
    }
    const handleExit = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) onClose();
    };
    document.addEventListener('fullscreenchange', handleExit);
    document.addEventListener('webkitfullscreenchange', handleExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleExit);
      document.removeEventListener('webkitfullscreenchange', handleExit);
      if (window.screen?.orientation?.unlock) {
        try { window.screen.orientation.unlock(); } catch {}
      }
    };
  }, [onClose]);

  const closeAndExit = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
    } catch {}
    onClose();
  };

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[400] bg-black flex items-center justify-center"
      data-testid="yoga-fullscreen-player"
    >
      {video.source === 'youtube' ? (
        <iframe
          src={`https://www.youtube.com/embed/${video.value}?autoplay=1&modestbranding=1&rel=0&playsinline=1`}
          title={video.label}
          className="w-full h-full"
          style={{ aspectRatio: '16/9', maxWidth: '100vw', maxHeight: '100vh' }}
          frameBorder="0"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      ) : (
        <video
          src={`${API}${video.value}`}
          controls
          autoPlay
          playsInline
          className="w-full h-full object-contain"
        />
      )}
      <button
        onClick={closeAndExit}
        className="absolute top-3 right-3 z-10 p-2 rounded-full bg-black/70 hover:bg-black text-white border border-white/20"
        data-testid="yoga-fullscreen-close"
        aria-label="Quitter le plein écran"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

function VideoCard({ video, isAdmin, onLock, onDelete, onPlayFs }) {
  const isYT = video.source === 'youtube';
  return (
    <div className="rounded-xl border border-white/10 bg-black/40 overflow-hidden" data-testid={`yoga-video-${video.id}`}>
      <div className="relative bg-black aspect-video">
        {isYT ? (
          <iframe
            src={`https://www.youtube.com/embed/${video.value}?modestbranding=1&rel=0&playsinline=1`}
            title={video.label}
            className="absolute inset-0 w-full h-full"
            frameBorder="0"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
          />
        ) : (
          <video
            src={`${API}${video.value}`}
            controls
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <button
          onClick={() => onPlayFs(video)}
          className="absolute top-1.5 right-1.5 p-1.5 rounded-full bg-black/75 hover:bg-black text-white border border-white/20"
          title="Plein écran"
          data-testid={`yoga-video-fs-${video.id}`}
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-2 flex items-center gap-2">
        <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 uppercase tracking-wider">
          {isYT ? 'YT' : 'MP4'}
        </span>
        <p className="text-white/85 text-xs flex-1 truncate" title={video.label}>{video.label}</p>
      </div>
      {isAdmin && (
        <div className="flex gap-1 px-2 pb-2">
          <button
            onClick={() => onLock(video)}
            className={`flex-1 text-[10px] flex items-center justify-center gap-1 px-2 py-1 rounded-md border transition ${
              video.locked
                ? 'bg-amber-500 text-slate-900 border-amber-400'
                : 'bg-slate-800 text-white/70 border-white/10 hover:bg-slate-700'
            }`}
            data-testid={`yoga-video-lock-${video.id}`}
          >
            {video.locked ? <><Lock className="w-3 h-3" /> Verrouillée</> : <><Unlock className="w-3 h-3" /> Verrouiller</>}
          </button>
          <button
            onClick={() => onDelete(video)}
            disabled={video.locked}
            className="flex-1 text-[10px] flex items-center justify-center gap-1 px-2 py-1 rounded-md border bg-red-500/15 text-red-300 border-red-500/30 hover:bg-red-500/25 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid={`yoga-video-delete-${video.id}`}
          >
            <Trash2 className="w-3 h-3" /> Supprimer
          </button>
        </div>
      )}
    </div>
  );
}

export default function YogaVideoGallery({ poseId, isAdmin = false }) {
  const { videos, loading, addYouTube, uploadFile, toggleLock, remove } = useYogaVideos(poseId);
  const [fsVideo, setFsVideo] = useState(null);
  const [ytInput, setYtInput] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleAddYT = async () => {
    const id = extractYouTubeId(ytInput);
    if (!id) { toast.error('URL ou ID YouTube invalide'); return; }
    setBusy(true);
    try { await addYouTube(id, ytInput); setYtInput(''); toast.success('Vidéo ajoutée'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setBusy(false); }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 60 * 1024 * 1024) { toast.error('Vidéo trop volumineuse (60 Mo max)'); return; }
    setBusy(true);
    try { await uploadFile(file, file.name); toast.success('Vidéo uploadée'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setBusy(false); }
  };

  const handleLock = async (v) => {
    try { await toggleLock(v.id); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action impossible'); }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Supprimer "${v.label}" ?`)) return;
    try { await remove(v.id); toast.success('Vidéo supprimée'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
  };

  return (
    <div className="space-y-3" data-testid={`yoga-video-gallery-${poseId}`}>
      <div className="flex items-center gap-2 text-cyan-300 text-sm font-medium">
        <Video className="w-4 h-4" /> Vidéos · {videos.length}
      </div>

      {isAdmin && (
        <div className="rounded-xl border border-amber-400/25 bg-amber-500/5 p-3 space-y-2" data-testid="yoga-admin-bar">
          <div className="flex items-center gap-2">
            <input
              type="text" value={ytInput} onChange={(e) => setYtInput(e.target.value)}
              placeholder="Coller un lien YouTube"
              className="flex-1 bg-slate-800/70 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs focus:border-amber-400/50 focus:outline-none"
              data-testid="yoga-yt-input"
            />
            <button onClick={handleAddYT} disabled={busy}
              className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-slate-900 font-semibold hover:bg-amber-400 disabled:opacity-50 flex items-center gap-1"
              data-testid="yoga-yt-add">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Youtube className="w-3 h-3" />}
              <span>Ajouter</span>
            </button>
          </div>
          <input ref={fileRef} type="file" accept="video/*" className="hidden"
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="yoga-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy}
            className="w-full text-xs flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800/70 text-white/80 border border-white/10 hover:bg-slate-700 disabled:opacity-50"
            data-testid="yoga-file-upload">
            <Upload className="w-3.5 h-3.5" /> Uploader une vidéo MP4
          </button>
        </div>
      )}

      {loading ? (
        <p className="text-white/40 text-xs italic">Chargement…</p>
      ) : videos.length === 0 ? (
        <p className="text-white/40 text-xs italic">Aucune vidéo pour le moment.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} isAdmin={isAdmin}
              onPlayFs={setFsVideo} onLock={handleLock} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {fsVideo && <FullscreenPlayer video={fsVideo} onClose={() => setFsVideo(null)} />}
    </div>
  );
}
